<template>
  <div class="container mt-4 mb-5" style="max-width: 1140px">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h3 class="m-0">Administration: Rollen und Berechtigungen</h3>
      <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'Invoices' }">Zurück</router-link>
    </div>

    <div v-if="!canReadRoles" class="alert alert-warning">
      Keine Berechtigung zum Lesen der Rollenverwaltung.
    </div>

    <div v-if="canReadRoles && canWriteRoles" class="card mb-4">
      <div class="card-body">
        <h5 class="card-title mb-1">Neue Firmenrolle</h5>
        <p class="text-muted small mb-3">Benutzerdefinierte Rollen gelten immer innerhalb einer Firma.</p>
        <div class="row g-2 align-items-end">
          <div class="col-md-4">
            <label class="form-label" for="roleName">Name *</label>
            <input id="roleName" v-model="createForm.name" class="form-control" type="text" />
          </div>
          <div class="col-md-6">
            <label class="form-label" for="roleDesc">Beschreibung</label>
            <input id="roleDesc" v-model="createForm.description" class="form-control" type="text" />
          </div>
          <div class="col-md-2">
            <button class="btn btn-primary w-100" type="button" :disabled="!createForm.name" @click="createRole">
              Erstellen
            </button>
          </div>
        </div>
      </div>
    </div>

    <DefinitionCopyPanel v-if="copySource" :key="copySource.id" title="Rolle" :source-name="copySource.name"
      scope="Die Kopie ist eine neue Firmenrolle." assignment-label="Berechtigungen" :assignments="copyAssignments"
      :busy="copyBusy" :error="copyError" @confirm="confirmCopy" @cancel="copySource = null" />

    <div v-if="canReadRoles" class="row g-4">
      <div class="col-lg-5">
        <div class="card h-100">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="card-title m-0">Rollen</h5>
              <button class="btn btn-outline-secondary btn-sm" type="button" @click="loadRoles">Neu laden</button>
            </div>

            <div class="table-responsive">
              <table class="table table-striped table-hover align-middle">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Geltungsbereich</th>
                    <th>Beschreibung</th>
                    <th class="text-end">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="role in roles"
                    :key="role.id"
                    :class="{ 'table-primary': role.id === selectedRoleId }"
                  >
                    <td>
                      <button class="btn btn-link p-0 text-decoration-none fw-semibold" type="button" @click="selectRole(role.id)">
                        <code>{{ role.name }}</code>
                      </button>
                    </td>
                    <td>
                      <span class="badge" :class="isGlobalRole(role) ? 'text-bg-dark' : 'text-bg-info'">
                        {{ isGlobalRole(role) ? "Global" : "Firma" }}
                      </span>
                    </td>
                    <td>{{ role.description || "-" }}</td>
                    <td class="text-end">
                      <div class="btn-group btn-group-sm">
                        <button class="btn btn-outline-primary" type="button" @click="selectRole(role.id)">
                          Matrix
                        </button>
                        <button
                          v-if="canWriteRoles && !isBuiltInRole(role)"
                          class="btn btn-outline-danger"
                          type="button"
                          @click="deleteRole(role)"
                        >
                          Löschen
                        </button>
                        <button v-if="canCopyRoles && !isAdministrativeRole(role)" class="btn btn-outline-secondary" type="button" :disabled="copyBusy" @click="prepareCopy(role)">Kopieren</button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="roles.length === 0">
                    <td colspan="4" class="text-center text-muted py-4">Keine Rollen gefunden.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-7">
        <div class="card h-100">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h5 class="card-title m-0">Berechtigungsmatrix</h5>
              <button
                class="btn btn-primary btn-sm"
                type="button"
                :disabled="!selectedRoleId || !canWritePermissions"
                @click="saveRolePermissions"
              >
                Speichern
              </button>
            </div>

            <div v-if="!canReadPermissions" class="alert alert-secondary mb-0">
              Keine Berechtigung zum Lesen der Berechtigungsmatrix.
            </div>

            <div v-else-if="!selectedRole" class="text-muted">
              Bitte eine Rolle auswählen, um Berechtigungen zu konfigurieren.
            </div>

            <template v-else>
              <div class="mb-3">
                <strong>Ausgewählte Rolle:</strong>
                <code class="ms-1">{{ selectedRole.name }}</code>
              </div>

              <div v-if="groupedPermissions.length === 0" class="text-muted">
                Keine Berechtigungen gefunden.
              </div>

              <div v-for="group in groupedPermissions" :key="group.category" class="mb-4">
                <h6 class="mb-2">{{ group.category }}</h6>
                <div class="table-responsive">
                  <table class="table table-sm table-striped align-middle">
                    <thead>
                      <tr>
                        <th style="width: 38%">Code</th>
                        <th style="width: 47%">Name</th>
                        <th style="width: 15%" class="text-center">Erlaubt</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="permission in group.items" :key="permission.id">
                        <td><code>{{ permission.code }}</code></td>
                        <td>{{ permission.name }}</td>
                        <td class="text-center">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :checked="isPermissionSelected(permission.id)"
                            :disabled="!canWritePermissions"
                            @change="togglePermission(permission.id, $event.target.checked)"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import AdminRoleService from "@/services/AdminRoleService";
import DefinitionCopyPanel from "@/components/DefinitionCopyPanel.vue";
import { useUserStore } from "@/stores/userStore";

const userStore = useUserStore();
const roles = ref([]);
const permissions = ref([]);
const selectedRoleId = ref(null);
const selectedPermissionIds = ref([]);
const createForm = reactive({ name: "", description: "" });

const BUILT_IN_ROLE_IDS = new Set([
  "00000000-0000-0000-0000-000000000010",
  "00000000-0000-0000-0000-000000000011",
  "00000000-0000-0000-0000-000000000012",
  "00000000-0000-0000-0000-000000000013",
]);

const canReadRoles = computed(() => userStore.hasPermission("admin.roles.read"));
const canWriteRoles = computed(() => userStore.hasPermission("admin.roles.write"));
const canReadPermissions = computed(() => userStore.hasPermission("admin.permissions.read"));
const canWritePermissions = computed(() => userStore.hasPermission("admin.permissions.write"));
const canCopyRoles = computed(() => canReadRoles.value && canWriteRoles.value && canReadPermissions.value && canWritePermissions.value);
const copySource = ref(null);
const copyAssignments = ref([]);
const copyBusy = ref(false);
const copyError = ref("");

function isAdministrativeRole(role) {
  return ["00000000-0000-0000-0000-000000000010", "00000000-0000-0000-0000-000000000011", "00000000-0000-0000-0000-000000000013"].includes(String(role.id).toLowerCase());
}

async function prepareCopy(role) {
  if (!canCopyRoles.value || copyBusy.value || isAdministrativeRole(role)) return;
  copyBusy.value = true;
  copyError.value = "";
  copySource.value = null;
  try {
    const response = await AdminRoleService.getRolePermissions(role.id);
    copyAssignments.value = (response.data || []).map(id => ({ id, label: permissions.value.find(p => p.id === id)?.code || id }));
    copySource.value = role;
  } finally { copyBusy.value = false; }
}

async function confirmCopy(name) {
  if (!canCopyRoles.value || copyBusy.value || !copySource.value) return;
  copyBusy.value = true;
  copyError.value = "";
  try {
    const response = await AdminRoleService.copyRole(copySource.value.id, name, copyAssignments.value.map(p => p.id));
    copySource.value = null;
    await loadRoles();
    await selectRole(response.data.id);
  } catch (error) {
    copyError.value = typeof error.response?.data === "string" ? error.response.data : "Die Rolle konnte nicht kopiert werden. Bitte Quelle und Berechtigungen erneut prüfen.";
  } finally { copyBusy.value = false; }
}

const selectedRole = computed(() => roles.value.find((role) => role.id === selectedRoleId.value) || null);
const groupedPermissions = computed(() => {
  const groups = new Map();
  for (const permission of permissions.value) {
    const category = permission.category || "General";
    if (!groups.has(category)) {
      groups.set(category, []);
    }
    groups.get(category).push(permission);
  }

  return Array.from(groups.entries())
    .sort((a, b) => a[0].localeCompare(b[0], "de", { sensitivity: "base" }))
    .map(([category, items]) => ({
      category,
      items: [...items].sort((a, b) => String(a.code || "").localeCompare(String(b.code || ""), "de", { sensitivity: "base" })),
    }));
});

function isBuiltInRole(role) {
  return BUILT_IN_ROLE_IDS.has(String(role?.id || "").toLowerCase());
}

function isGlobalRole(role) {
  const id = String(role?.id || "").toLowerCase();
  return id === "00000000-0000-0000-0000-000000000010"
    || id === "00000000-0000-0000-0000-000000000013";
}

function isPermissionSelected(permissionId) {
  return selectedPermissionIds.value.includes(permissionId);
}

function togglePermission(permissionId, enabled) {
  const next = new Set(selectedPermissionIds.value);
  if (enabled) {
    next.add(permissionId);
  } else {
    next.delete(permissionId);
  }
  selectedPermissionIds.value = Array.from(next);
}

async function loadRoles() {
  if (!canReadRoles.value) {
    roles.value = [];
    return;
  }

  const response = await AdminRoleService.getRoles();
  roles.value = Array.isArray(response?.data) ? response.data : [];
}

async function loadPermissions() {
  if (!canReadPermissions.value) {
    permissions.value = [];
    return;
  }

  const response = await AdminRoleService.getPermissions();
  permissions.value = Array.isArray(response?.data) ? response.data : [];
}

async function loadRolePermissions() {
  if (!selectedRoleId.value || !canReadPermissions.value) {
    selectedPermissionIds.value = [];
    return;
  }

  const response = await AdminRoleService.getRolePermissions(selectedRoleId.value);
  selectedPermissionIds.value = Array.isArray(response?.data) ? response.data : [];
}

async function selectRole(roleId) {
  selectedRoleId.value = roleId;
  await loadRolePermissions();
}

async function createRole() {
  if (!canWriteRoles.value) return;
  await AdminRoleService.createRole(createForm.name, createForm.description || null);
  createForm.name = "";
  createForm.description = "";
  await loadRoles();
}

async function deleteRole(role) {
  if (!canWriteRoles.value) return;
  const confirmed = window.confirm(`Rolle "${role.name}" wirklich löschen?`);
  if (!confirmed) return;

  await AdminRoleService.deleteRole(role.id);
  if (selectedRoleId.value === role.id) {
    selectedRoleId.value = null;
    selectedPermissionIds.value = [];
  }
  await loadRoles();
}

async function saveRolePermissions() {
  if (!canWritePermissions.value || !selectedRoleId.value) return;
  await AdminRoleService.setRolePermissions(selectedRoleId.value, selectedPermissionIds.value);
}

onMounted(async () => {
  if (!userStore.me) {
    try {
      await userStore.loadMe();
    } catch (error) {
      console.error("Failed to load /user/me:", error);
    }
  }

  await Promise.all([loadRoles(), loadPermissions()]);

  if (roles.value.length > 0) {
    await selectRole(roles.value[0].id);
  }
});
</script>
